<template>
    <transition name="fade" mode="out-in">
        <base-modal v-if="visible" :title="modalTitle" height="85vh" @close="$emit('close')">
            <div class="tier-drilldown flex flex-col h-full min-h-0 px-8 pt-4">
                <div v-if="!loading && hasRows && showExportExcel" class="flex justify-end mb-3">
                    <button type="button" class="tier-drilldown-export-btn" @click.stop="exportExcel">
                        <font-awesome-icon :icon="['fas', 'file-excel']" class="tier-drilldown-export-btn__icon" />
                        <span>Export Excel</span>
                    </button>
                </div>
                <div v-if="loading" class="flex flex-1 min-h-0 items-center justify-center py-16">
                    <base-spinner></base-spinner>
                </div>
                <div v-else-if="hasRows" class="flex-1 flex flex-col min-h-0 overflow-hidden">
                    <div class="overflow-x-auto overflow-y-auto flex-1 border rounded-lg">
                        <table class="min-w-full text-left text-sm">
                            <thead class="tier-drilldown-head border-b sticky top-0">
                                <tr>
                                    <th class="px-4 py-2 text-gray-600">#</th>
                                    <th class="px-4 py-2 text-gray-600">Name</th>
                                    <th class="px-4 py-2 text-gray-600">Entity</th>
                                    <th class="px-4 py-2 text-gray-600">Industry</th>
                                    <th class="px-4 py-2 text-gray-600">Type of collaboration</th>
                                    <th class="px-4 py-2 text-gray-600">Classifications</th>
                                    <th class="px-4 py-2 text-gray-600">Tier</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, idx) in rows" :key="row.name + '-tier-dd-' + idx"
                                    class="border-b border-gray-100">
                                    <td class="px-4 py-2 whitespace-nowrap">{{ firstItem + idx }}</td>
                                    <td class="px-4 py-2">{{ row.name }}</td>
                                    <td class="px-4 py-2 whitespace-nowrap">{{ formatEntity(row.entity) }}</td>
                                    <td class="px-4 py-2">{{ (row.industry_relation && row.industry_relation.name) ? row.industry_relation.name : "—" }}</td>
                                    <td class="px-4 py-2">{{ row.type_of_collaboration || "—" }}</td>
                                    <td class="px-4 py-2">{{ formatClassifications(row) }}</td>
                                    <td class="px-4 py-2 whitespace-nowrap">
                                        <span v-if="rowTier(row)" :class="partnerTierBadgeClass(rowTier(row))">{{ rowTier(row) }}</span>
                                        <span v-else>—</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="mt-3 shrink-0">
                        <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                    </div>
                </div>
                <div v-else class="flex flex-1 min-h-0 items-center justify-center py-16 text-gray-500">
                    No partners found.
                </div>
            </div>
        </base-modal>
    </transition>
</template>

<script>
import gql from "graphql-tag";
import xlsx from "xlsx/dist/xlsx.full.min.js";
import Pagination from "../../components/shared/pagination.vue";
import { evaluatePartnerTier, getTierBadgeClass } from "../../helpers/partnerTier";

var PARTNERS_QUERY = gql`
    query TierDrilldownCurrentPartners(
        $first: Int!
        $page: Int!
        $tier: [String]
        $is_pipeline: Boolean
        $entity: String
        $industry: Int
        $type_of_collaboration: [String]
        $classifications: [String]
        $status: String
        $search: String
    ) {
        current_partners(
            first: $first
            page: $page
            tier: $tier
            is_pipeline: $is_pipeline
            entity: $entity
            industry: $industry
            type_of_collaboration: $type_of_collaboration
            classifications: $classifications
            status: $status
            search: $search
        ) {
            error
            error_code
            message
            data {
                name
                entity
                industry
                industry_relation { id name }
                type_of_collaboration
                classifications
            }
            paginatorInfo {
                count
                currentPage
                firstItem
                hasMorePages
                lastItem
                lastPage
                perPage
                total
            }
        }
    }
`;

var EXPORT_QUERY = gql`
    query TierDrilldownExportCurrentPartners(
        $page: Int
        $limit: Int
        $tier: [String]
        $is_pipeline: Boolean
        $entity: String
        $industry: Int
        $type_of_collaboration: [String]
        $classifications: [String]
        $status: String
        $search: String
    ) {
        exportCurrentPartners(
            page: $page
            limit: $limit
            tier: $tier
            is_pipeline: $is_pipeline
            entity: $entity
            industry: $industry
            type_of_collaboration: $type_of_collaboration
            classifications: $classifications
            status: $status
            search: $search
        ) {
            error
            error_code
            message
            data {
                name
                entity
                industry
                industry_relation { id name }
                type_of_collaboration
                classifications
            }
        }
    }
`;

function runDetachedExport(ctx) {
    var store = ctx.store;
    var apolloClient = ctx.apolloClient;
    var jobId = ctx.jobId;
    var fileName = ctx.fileName;
    var variables = ctx.variables;
    var columns = ctx.columns;
    var mapRow = ctx.mapRow;

    var allRows = [];
    var page = 1;
    var limit = 200;

    function fetchNext() {
        var vars = {};
        var keys = Object.keys(variables);
        for (var i = 0; i < keys.length; i++) {
            vars[keys[i]] = variables[keys[i]];
        }
        vars.page = page;
        vars.limit = limit;

        apolloClient
            .query({
                query: EXPORT_QUERY,
                fetchPolicy: "network-only",
                variables: vars,
            })
            .then(function (result) {
                var responseData = result && result.data
                    ? result.data.exportCurrentPartners
                    : null;

                if (!responseData || responseData.error) {
                    throw new Error(
                        (responseData && responseData.message) || "Server error"
                    );
                }

                if (responseData.data && responseData.data.length > 0) {
                    for (var i = 0; i < responseData.data.length; i++) {
                        allRows.push(mapRow(responseData.data[i]));
                    }

                    var percent = Math.min(95, Math.round(
                        (allRows.length / Math.max(allRows.length + limit, 1)) * 100
                    ));

                    store.dispatch("exportManager/updateJob", {
                        id: jobId,
                        progress: percent,
                        currentSheet: "Partners (" + allRows.length + " rows)",
                    });

                    page++;
                    fetchNext();
                } else {
                    finishExport();
                }
            })
            .catch(function (error) {
                if (allRows.length > 0) {
                    finishExport();
                } else {
                    store.dispatch("exportManager/failJob", {
                        id: jobId,
                        error: (error && error.message) || "Export failed",
                    });
                }
            });
    }

    function finishExport() {
        try {
            if (allRows.length === 0) {
                store.dispatch("exportManager/failJob", {
                    id: jobId,
                    error: "No data available for export",
                });
                return;
            }

            var colKeys = columns.map(function (col) { return col.key; });

            var rows = allRows.map(function (row) {
                var out = {};
                for (var i = 0; i < columns.length; i++) {
                    var key = columns[i].key;
                    out[key] = row[key] !== undefined && row[key] !== null ? row[key] : "";
                }
                return out;
            });

            var worksheet = xlsx.utils.json_to_sheet(rows, { header: colKeys });

            var columnWidths = [];
            for (var i = 0; i < columns.length; i++) {
                var column = String.fromCharCode(65 + (i % 26));
                var r = Math.floor(i / 26);
                var columnLetter = r > 0 ? String.fromCharCode(64 + r) + column : column;
                worksheet[columnLetter + "1"] = { v: columns[i].header, t: "s" };
                columnWidths.push({ width: Math.max(40, columns[i].header.length + 5) });
            }

            worksheet["!cols"] = columnWidths;

            var workbook = xlsx.utils.book_new();
            xlsx.utils.book_append_sheet(workbook, worksheet, "Partners");
            xlsx.writeFile(workbook, fileName);

            store.dispatch("exportManager/completeJob", {
                id: jobId,
                rowCount: allRows.length,
            });
        } catch (error) {
            store.dispatch("exportManager/failJob", {
                id: jobId,
                error: (error && error.message) || "Export failed",
            });
        }
    }

    fetchNext();
}

export default {
    name: "TierDrilldownModal",
    components: {
        Pagination: Pagination,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        dimension: {
            type: String,
            default: "tier",
        },
        dimensionValue: {
            default: null,
        },
        dimensionLabel: {
            type: String,
            default: "",
        },
        tier: {
            type: String,
            default: "",
        },
        baseFilters: {
            type: Object,
            default: function () {
                return {};
            },
        },
        showExportExcel: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["close"],
    data: function () {
        return {
            loading: false,
            partners: null,
            pageSize: 10,
        };
    },
    computed: {
        modalTitle: function () {
            var label = this.resolvedLabel;
            if (label) {
                return label + " Partners";
            }
            return "Partners";
        },
        resolvedDimension: function () {
            if (this.dimension) {
                return this.dimension;
            }
            return "tier";
        },
        resolvedValue: function () {
            if (this.dimensionValue !== null && this.dimensionValue !== undefined && this.dimensionValue !== "") {
                return this.dimensionValue;
            }
            return this.tier || null;
        },
        resolvedLabel: function () {
            if (this.dimensionLabel) {
                return this.dimensionLabel;
            }
            if (this.resolvedValue) {
                return String(this.resolvedValue);
            }
            return "";
        },
        drilldownKey: function () {
            return this.resolvedDimension + ":" + String(this.resolvedValue || "");
        },
        rows: function () {
            if (!this.partners || !this.partners.data) {
                return [];
            }
            return this.partners.data;
        },
        hasRows: function () {
            if (this.partners && this.partners.paginatorInfo && this.partners.paginatorInfo.total > 0) {
                return true;
            }
            return this.rows.length > 0;
        },
        paginatorInfo: function () {
            if (this.partners && this.partners.paginatorInfo) {
                return this.partners.paginatorInfo;
            }
            return {
                count: 0,
                currentPage: 1,
                firstItem: 0,
                hasMorePages: false,
                lastItem: 0,
                lastPage: 1,
                perPage: this.pageSize,
                total: 0,
            };
        },
        firstItem: function () {
            var info = this.paginatorInfo;
            return info && info.firstItem ? info.firstItem : 1;
        },
        queryVariables: function () {
            var merged = this.baseFilters || {};
            var cleaned = {};
            var keys = Object.keys(merged);

            for (var i = 0; i < keys.length; i++) {
                var key = keys[i];
                var val = merged[key];
                if (val === undefined || val === null || val === "") {
                    continue;
                }
                if (Array.isArray(val) && val.length === 0) {
                    continue;
                }
                cleaned[key] = val;
            }

            var dim = this.resolvedDimension;
            var dimVal = this.resolvedValue;
            if (dimVal !== null && dimVal !== undefined && dimVal !== "") {
                if (dim === "tier") {
                    cleaned.tier = [dimVal];
                } else if (dim === "entity") {
                    cleaned.entity = dimVal;
                } else if (dim === "industry") {
                    cleaned.industry = parseInt(dimVal, 10);
                } else if (dim === "type_of_collaboration") {
                    cleaned.type_of_collaboration = [dimVal];
                } else if (dim === "classification") {
                    cleaned.classifications = [dimVal];
                } else if (dim === "status") {
                    cleaned.status = dimVal;
                }
            }

            return cleaned;
        },
    },
    watch: {
        visible: function (isVisible) {
            if (isVisible) {
                this.fetchPartners(1);
            } else {
                this.partners = null;
            }
        },
        drilldownKey: function () {
            if (this.visible) {
                this.fetchPartners(1);
            }
        },
    },
    methods: {
        setPage: function (pageNo) {
            this.fetchPartners(pageNo);
        },
        formatEntity: function (entity) {
            if (!entity) {
                return "—";
            }
            return String(entity).replace(/ATHS/g, "ATS");
        },
        formatClassifications: function (row) {
            if (!row || !row.classifications || row.classifications.length === 0) {
                return "—";
            }
            return row.classifications.join(", ");
        },
        rowTier: function (row) {
            return evaluatePartnerTier(row);
        },
        partnerTierBadgeClass: function (tierValue) {
            return getTierBadgeClass(tierValue);
        },
        hasDimensionFilter: function () {
            var val = this.resolvedValue;
            if (val === null || val === undefined || val === "") {
                return false;
            }
            return true;
        },
        async fetchPartners(pageNo) {
            if (!this.hasDimensionFilter()) {
                this.partners = null;
                return;
            }

            this.loading = true;

            try {
                var response = await this.$apollo.query({
                    query: PARTNERS_QUERY,
                    variables: Object.assign(
                        {
                            first: this.pageSize,
                            page: pageNo || 1,
                        },
                        this.queryVariables
                    ),
                    fetchPolicy: "network-only",
                });

                var payload = response && response.data && response.data.current_partners
                    ? response.data.current_partners
                    : null;

                if (!payload || payload.error) {
                    throw new Error((payload && payload.message) ? payload.message : "Failed to load partners.");
                }

                this.partners = payload;
            } catch (error) {
                console.error("Failed to load drilldown partners:", error);
                this.partners = null;
            }

            this.loading = false;
        },
        exportExcel: function () {
            if (!this.hasDimensionFilter()) {
                return;
            }

            var store = this.$store;
            var apolloClient = this.$apollo && this.$apollo.provider
                ? this.$apollo.provider.defaultClient
                : null;
            if (!apolloClient) {
                return;
            }

            var label = this.resolvedLabel || "Partners";
            var safeLabel = String(label).replace(/[^\w-]+/g, "_");
            var fileName = safeLabel + "_Partners.xlsx";

            var variablesCopy = JSON.parse(JSON.stringify(this.queryVariables));

            var columns = [
                { key: "name", header: "NAME" },
                { key: "entity", header: "ENTITY" },
                { key: "industry", header: "INDUSTRY" },
                { key: "type_of_collaboration", header: "TYPE OF COLLABORATION" },
                { key: "classifications", header: "CLASSIFICATIONS" },
                { key: "tier", header: "TIER" },
            ];
            var mapRow = function (obj) {
                var classifications = "";
                if (obj && obj.classifications && obj.classifications.length > 0) {
                    classifications = obj.classifications.join(", ");
                }

                return {
                    name: obj.name || "",
                    entity: obj.entity ? String(obj.entity).replace(/ATHS/g, "ATS") : "",
                    industry: (obj.industry_relation && obj.industry_relation.name) ? obj.industry_relation.name : "",
                    type_of_collaboration: obj.type_of_collaboration || "",
                    classifications: classifications,
                    tier: evaluatePartnerTier(obj) || "",
                };
            };

            store
                .dispatch("exportManager/addJob", {
                    fileName: fileName,
                    totalSheets: 1,
                })
                .then(function (jobId) {
                    store.dispatch("exportManager/setMinimized", false);
                    store.dispatch("exportManager/updateJob", {
                        id: jobId,
                        progress: 5,
                        currentSheet: label + " Partners",
                    });

                    runDetachedExport({
                        store: store,
                        apolloClient: apolloClient,
                        jobId: jobId,
                        fileName: fileName,
                        variables: variablesCopy,
                        columns: columns,
                        mapRow: mapRow,
                    });
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.tier-drilldown-head {
    background-color: #f8fafc;
}

.partner-tier-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.625rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
}

.partner-tier-badge-strategic {
    background-color: #ede9fe;
    color: #5b21b6;
}

.partner-tier-badge-active {
    background-color: #dbeafe;
    color: #1d4ed8;
}

.partner-tier-badge-engaged {
    background-color: #fef3c7;
    color: #b45309;
}

.partner-tier-badge-shelved {
    background-color: #f1f5f9;
    color: #475569;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.tier-drilldown-export-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    white-space: nowrap;
    border-radius: 0.75rem;
    border: 1px solid rgba(16, 185, 129, 0.25);
    background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #052e1f;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.tier-drilldown-export-btn:hover {
    border-color: rgba(16, 185, 129, 0.45);
    background: linear-gradient(to bottom, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.14));
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.tier-drilldown-export-btn:disabled {
    pointer-events: none;
    opacity: 0.6;
}

.tier-drilldown-export-btn__icon {
    font-size: 1.05rem;
    color: #059669;
    filter: drop-shadow(0 1px 0 rgba(0, 0, 0, 0.06));
}
</style>
